import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {acceptedFileTypes} from '@/features/users/utils'
import {Label} from '@/components/ui/label/Label'
import {StyledUserInputEvaluation} from './style'
import {ChangeEvent} from 'react'
import {useUploadDocument} from '@/services/useUploadDocument'
import {toast} from '@/utilities/toast/toast'
import {useDeleteDocument} from '@/services/useDeleteDocument'
import {UploadedDocument} from '@/types'
import {FileUploader} from '@/components/commons/file-upload/FileUpload'

export const UserInputFmEvaluation = ({
    label,
    evaluation,
    document,
    onChangeEvaluation,
    onChangeDocument,
    isOptional
}: {
    label: string
    document?: UploadedDocument | null
    evaluation?: string | null
    onChangeEvaluation: (value: string) => void
    onChangeDocument: (value: UploadedDocument | null) => void
    isOptional?: boolean
}) => {
    const {t} = useTranslation()

    // mutation
    const uploadDocument = useUploadDocument({
        onSuccess: response => {
            onChangeDocument(response)
            toast.success(t('commons:upload_success'))
        },
        onError: () => toast.error(t('commons:upload_error'))
    })
    const deleteDocument = useDeleteDocument({
        onSuccess: () => {
            onChangeDocument(null)
        }
    })

    // actions
    const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
        // Keep only numbers
        let value = e.target.value.replace(/[^0-9]/g, '')

        // Remove leading zeros, except if the value is "0"
        if (value.length > 1 && value.startsWith('0')) {
            value = value.replace(/^0+/, '')
        }

        onChangeEvaluation(value)
    }

    return (
        <Flexbox direction="column" gap={1.5} fullWidth>
            {label && (
                <Label>
                    {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                </Label>
            )}
            <Flexbox fullWidth gap={2}>
                <StyledUserInputEvaluation
                    type="text"
                    placeholder="0"
                    value={evaluation ?? undefined}
                    width="100%"
                    onChange={handleInput}
                />

                <FileUploader
                    document={document}
                    isLoading={uploadDocument.isPending || deleteDocument.isPending}
                    onDeleteFile={deleteDocument.mutate}
                    dropDownMenuProps={{
                        triggerWidth: '100%'
                    }}
                    inputFileProps={{
                        acceptedFiles: acceptedFileTypes,
                        maxSizeInMB: 8,
                        hideFilename: true,
                        width: 'auto',
                        onAcceptFiles: file => {
                            const formData = new FormData()
                            formData.append(`file`, file)
                            uploadDocument.mutate(formData)
                        }
                    }}
                />
            </Flexbox>
        </Flexbox>
    )
}
